import { CeiTableColumn, CeiTablePage } from '@cei-ufg/ngx-arquitetura-cei-frontend';

/** Mínimo de caracteres considerado (cabeçalho "ID"). */
const MIN_DIGITOS_COLUNA_ID = 2;

/** Espaço lateral fixo além dos dígitos (padding interno da célula). */
const PADDING_COLUNA_ID = '1.25rem';

/**
 * Calcula largura CSS da coluna ID proporcional ao maior número de dígitos exibido.
 * Usa `ch` para escalar só quando o conteúdo cresce.
 */
export function calcularLarguraColunaId(maxDigitos: number): string {
  const digitos = Math.max(MIN_DIGITOS_COLUNA_ID, maxDigitos);
  return `calc(${digitos}ch + ${PADDING_COLUNA_ID})`;
}

/** Obtém o maior número de dígitos entre os valores de ID da página atual. */
export function extrairMaxDigitosId(
  itens: readonly unknown[],
  propriedadeId = 'id',
  maxAtual = MIN_DIGITOS_COLUNA_ID,
): number {
  let max = maxAtual;
  for (const item of itens) {
    const valor = (item as Record<string, unknown> | null | undefined)?.[propriedadeId];
    if (valor === null || valor === undefined) {
      continue;
    }
    const digitos = String(valor).length;
    if (digitos > max) {
      max = digitos;
    }
  }
  return max;
}

/** Aplica largura adaptável à coluna `id`. */
export function aplicarLarguraColunaId<T>(
  colunas: CeiTableColumn<T>[],
  maxDigitos = MIN_DIGITOS_COLUNA_ID,
): CeiTableColumn<T>[] {
  const largura = calcularLarguraColunaId(maxDigitos);
  return colunas.map((coluna) =>
    coluna.key === 'id' ? { ...coluna, width: largura } : coluna,
  );
}

/**
 * Acumula o maior ID já carregado (scroll infinito) e recalcula a largura só quando necessário.
 */
export class GerenciadorLarguraColunaId {
  private maxDigitos = MIN_DIGITOS_COLUNA_ID;

  /** Registra itens de uma página; retorna true se a largura deve ser recalculada. */
  registrarPagina(itens: readonly unknown[], propriedadeId = 'id'): boolean {
    const novoMax = extrairMaxDigitosId(itens, propriedadeId, this.maxDigitos);
    if (novoMax === this.maxDigitos) {
      return false;
    }
    this.maxDigitos = novoMax;
    return true;
  }

  /** Reaplica a largura atual sobre a definição base das colunas. */
  aplicar<T>(colunasBase: readonly CeiTableColumn<T>[]): CeiTableColumn<T>[] {
    return aplicarLarguraColunaId([...colunasBase], this.maxDigitos);
  }
}

/**
 * Recalcula colunas após carregar página; retorna null se a largura não mudou.
 */
export function atualizarColunasIdAoCarregarPagina<T>(
  gerenciador: GerenciadorLarguraColunaId,
  colunasBase: readonly CeiTableColumn<T>[],
  pagina: CeiTablePage<T>,
): CeiTableColumn<T>[] | null {
  const colunaId = colunasBase.find((coluna) => coluna.key === 'id');
  const propriedadeId = colunaId?.property ?? 'id';
  if (!gerenciador.registrarPagina(pagina.results, propriedadeId)) {
    return null;
  }
  return gerenciador.aplicar(colunasBase);
}
